/* eslint-disable @typescript-eslint/no-unused-vars */
import type { Ref } from 'react';
import { forwardRef, useCallback, useMemo } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import PopupState, { bindToggle, bindPopper } from 'material-ui-popup-state';
import type { PopupState as PopupStateProps } from 'material-ui-popup-state/hooks';
import { PopperMenu, PopperMenuItem } from '@trinity/chat-components/components/Popper/index.js';
import { useFolderActions, useFolderMenuItems } from '@/containers/Workspace.Folder/hooks';
import { MoreHorizontalAnchorSmall } from '../presentation/Anchor';
import type { FolderActionsMenuProps, FolderActionsPopperProps } from './interfaces';

const FolderActionsContextMenu = forwardRef(({ onClose }: FolderActionsMenuProps, ref: Ref<HTMLDivElement>) => {
  const valid = useFolderMenuItems();
  const actions = useFolderActions();

  const handleClick = useCallback((fn: () => unknown) => () => {
    onClose();
    return fn();
  }, [onClose]);

  const remove = useMemo(() => {
    return {
      children: 'Remove',
      onClick: actions.delete,
    };
  }, [actions.delete]);

  const ownership = useMemo(() => {
    return {
      children: 'Change Owner',
      onClick: actions.changeOwner,
    };
  }, [actions.changeOwner]);

  const move = useMemo(() => {
    return {
      children: 'Move',
      onClick: actions.move,
    };
  }, [
    actions.move,
  ]);

  const rename = useMemo(() => {
    return {
      children: 'Rename',
      onClick: actions.rename,
    };
  }, [actions.rename]);


  const manageAccess = useMemo(() => {
    return {
      children: 'Manage Access',
      onClick: actions.manageAccess,
    };
  }, [actions.manageAccess]);

  const items = useMemo(() => {
    return [
      // valid.canChangeOwner && ownership,
      valid.canMove && move,
      valid.canDelete && remove,
      valid.canRename && rename,
      valid.canManageAccess && manageAccess,
    ].filter(Boolean);
  }, [
    // nestedMenus,
    valid,
    manageAccess,
    move,
    // ownership,
    remove,
    rename,
  ]);

  return (
    <>
      <PopperMenu ref={ref}>
        {items.map((item, i) => (
          <PopperMenuItem
            key={i}
            onClick={handleClick(item.onClick)}>
            {item.children}
          </PopperMenuItem>
        ))}
      </PopperMenu>
    </>
  );
});

export const FolderActionsContextMenuPopper = ({
  placement = 'bottom-end',
  renderAnchor,
}: FolderActionsPopperProps) => {


  const defaultAnchor = useCallback((state: PopupStateProps) => {
    return (
      <MoreHorizontalAnchorSmall open={state.isOpen} />
    );
  }, []);

  return (
    <>
      <PopupState variant="popper" popupId="folder-actions-popper">
        {popupState => (
          <div>
            <div {...bindToggle(popupState)}>
              {(renderAnchor || defaultAnchor)(popupState)}
            </div>
            <Popper
              {...bindPopper(popupState)}
              placement={placement}>
              <ClickAwayListener
                onClickAway={() => popupState.close()}>
                <FolderActionsContextMenu
                  onClose={popupState.close} />
              </ClickAwayListener>
            </Popper>
          </div>
        )}
      </PopupState>
    </>
  );
};